import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, ExternalLink, Copy, Send, Loader2, Archive, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { ChangeReasonDialog } from "./ChangeReasonDialog";
import { SendProgrammeEmailDialog } from "./SendProgrammeEmailDialog";
import { PRODUCTION_DOMAIN } from "@/lib/constants";
import { PreparedForInlineEdit } from "./PreparedForInlineEdit";

interface ProgrammeViewerProps {
  leadId: string;
  clientEmail?: string;
  clientName?: string;
  leadInboundEmailId?: string | null;
}

export const ProgrammeViewer = ({ leadId, clientEmail, clientName, leadInboundEmailId }: ProgrammeViewerProps) => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programmeToDelete, setProgrammeToDelete] = useState<{ id: string; number: string } | null>(null);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [programmeToSend, setProgrammeToSend] = useState<{ id: string; number: string; shareToken: string } | null>(null);

  const [showArchived, setShowArchived] = useState(false);
  const { data: allProgrammes } = useQuery({
    queryKey: ["programmes", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotations")
        .select("*")
        .eq("lead_id", leadId)
        .eq("document_type", "programme")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
  const programmes = (allProgrammes || []).filter((p: any) => !p.archived_at);
  const archivedProgrammes = (allProgrammes || []).filter((p: any) => !!p.archived_at);

  // Programmes are archived, never erased: hidden, link disabled, restorable.
  const deleteProgrammeMutation = useMutation({
    mutationFn: async ({ programmeId, reason }: { programmeId: string; reason: string }) => {
      const { error } = await (supabase as any).rpc("archive_quotation", {
        p_quotation_id: programmeId,
        p_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programmes", leadId] });
      toast.success("Programme archived — you can restore it from Archived programmes");
      setDeleteDialogOpen(false);
      setProgrammeToDelete(null);
    },
    onError: (error: any) => {
      console.error("Archive programme error:", error);
      toast.error(error?.message || "Failed to archive programme");
    },
  });

  const restoreProgrammeMutation = useMutation({
    mutationFn: async (programmeId: string) => {
      const { error } = await (supabase as any).rpc("restore_quotation", { p_quotation_id: programmeId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programmes", leadId] });
      toast.success("Programme restored — its link works again");
    },
    onError: (error: any) => toast.error(error?.message || "Failed to restore programme"),
  });

  if (!allProgrammes || allProgrammes.length === 0) {
    return null;
  }

  const handleDeleteClick = (programmeId: string, programmeNumber: string) => {
    setProgrammeToDelete({ id: programmeId, number: programmeNumber });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (reason: string) => {
    if (!programmeToDelete) return;
    deleteProgrammeMutation.mutate({ programmeId: programmeToDelete.id, reason });
  };

  const handleCopyLink = (shareToken: string | null, programmeNumber: string) => {
    if (!shareToken) {
      toast.error("Share token not available for this programme");
      return;
    }
    const link = `${PRODUCTION_DOMAIN}/programme/${shareToken}`;
    navigator.clipboard.writeText(link);
    toast.success(`Link copied for ${programmeNumber}!`);
  };

  const handleSendClick = (programmeId: string, programmeNumber: string, shareToken: string | null) => {
    if (!shareToken) {
      toast.error("Share token not available for this programme");
      return;
    }
    setProgrammeToSend({ id: programmeId, number: programmeNumber, shareToken });
    setSendEmailDialogOpen(true);
  };

  return (
    <>
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Travel Programmes</h2>
        <div className="space-y-3">
          {programmes.map((programme) => (
            <div
              key={programme.id}
              className="flex items-center justify-between p-4 rounded-lg bg-muted"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="font-medium">{programme.quote_number}</span>
                  {(programme as any).is_white_label && (
                    <Badge variant="outline" className="text-xs">White Label</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Created: {format(new Date(programme.created_at), "MMM d, yyyy")}
                </p>
                {(programme as any).programme_title && (
                  <p className="text-sm mt-1">
                    {(programme as any).programme_title}
                  </p>
                )}
                <PreparedForInlineEdit
                  quotationId={programme.id}
                  value={(programme as any).prepared_for}
                  fallbackName={clientName}
                  invalidateKeys={[["programmes", leadId]]}
                />
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                {programme.share_token ? (
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a
                      href={`/programme/${programme.share_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View
                    </a>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toast.error("Share token not available for this programme")}
                    className="gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyLink(programme.share_token, programme.quote_number)}
                  className="gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendClick(programme.id, programme.quote_number, programme.share_token)}
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  Send
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Archive programme"
                  aria-label="Archive programme"
                  onClick={() => handleDeleteClick(programme.id, programme.quote_number)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Archive className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {programmes.length === 0 && (
            <p className="text-sm text-muted-foreground">No active programmes. Archived programmes are listed below.</p>
          )}
        </div>
        {archivedProgrammes.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 h-8 px-2 text-muted-foreground"
              onClick={() => setShowArchived((v) => !v)}
            >
              <Archive className="h-4 w-4" />
              Archived programmes ({archivedProgrammes.length})
              {showArchived ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            {showArchived && (
              <div className="mt-2 space-y-2">
                {archivedProgrammes.map((p: any) => (
                  <div
                    key={p.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-dashed bg-muted/40 px-3 py-2"
                  >
                    <span className="font-medium text-sm whitespace-nowrap">{p.quote_number}</span>
                    <span className="text-xs text-muted-foreground min-w-0 flex-1 break-words">
                      Archived {format(new Date(p.archived_at), "d MMM yyyy, HH:mm")}
                      {p.archived_reason ? ` · ${p.archived_reason}` : ""}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 h-8 ml-auto"
                      disabled={restoreProgrammeMutation.isPending}
                      onClick={() => restoreProgrammeMutation.mutate(p.id)}
                    >
                      <RotateCcw className="h-4 w-4" />
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <ChangeReasonDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        title="Archive this programme?"
        description={`${programmeToDelete?.number || 'This programme'} will be hidden from the lead and its link will stop working. You can restore it any time. Please explain why.`}
      />

      {programmeToSend && (
        <SendProgrammeEmailDialog
          open={sendEmailDialogOpen}
          onOpenChange={setSendEmailDialogOpen}
          leadId={leadId}
          programmeNumber={programmeToSend.number}
          shareToken={programmeToSend.shareToken}
          clientEmail={clientEmail || ""}
          clientName={clientName || ""}
          leadInboundEmailId={leadInboundEmailId}
          onSuccess={() => {
            setSendEmailDialogOpen(false);
            setProgrammeToSend(null);
          }}
        />
      )}
    </>
  );
};
